import React, { useState, useEffect, useContext, useMemo } from "react";
import styled from "styled-components";
import Request from "common/Request";
import {
  tooltipContentGeneratorForUnits,
  formats,
} from "common/graphs/NVStackedAreaChart";
import {
  NVLineChartDownsampled,
  NVStackedAreaChartDownsampled,
} from "common/graphs/DownSampling";
import RangeSliderInput from "common/RangeSliderInput";
import * as d3 from "d3";
import ReportContextControls from "../ReportContextControls";
import { retrieveRange } from "common/api";
import { useSearchParams } from "react-router-dom";
import { toObjectLiteral } from "common/utils";
import BqnContext from "common/BqnContext";
import { getFieldsLanes, getFieldsOf } from "../lanes";
import useHoursOfReturnView from "common/hooks/useHoursOfReturnView";

const UsageGraphLayout = styled.div`
  h4 {
    text-align: center;
    &.align-left {
      text-align: left;
    }
  }
`;
const CenteredMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 500px;
`;
const NoDataFound = ({ message = "No data found" }) => (
  <CenteredMessage>
    <h4>{message}</h4>
  </CenteredMessage>
);

const ReportResult = ({
  Totals = NoTotals,
  from = "speeds",
  itemsFrom,
  itemsValueFrom,
  graphTitle = null,
  yAxisUnits,
  yAxisPrecision,
  fixedYRange,
  range: wholeRange = null,
  context = {},
  noDataMessage = undefined,
  participants = undefined,
  onSeriesInspect = undefined,
  stacked = true,
  tooltipContentGenerator = undefined,
  ...result
}) => {
  const data = result[from];
  const [range, setRange] = useState(wholeRange);
  const onChange = (value) => setRange(value);
  const onInspect = onSeriesInspect === undefined
    ? undefined
    : (series) => onSeriesInspect(series, context);

  if (data === undefined || data.length === 0) {
    return <NoDataFound message={noDataMessage} />
  }

  return (
    <>
      <UsageGraph
        items={data}
        range={range}
        itemsFrom={itemsFrom}
        itemsValueFrom={itemsValueFrom}
        graphTitle={graphTitle}
        yAxisUnits={doUnits(yAxisUnits, context)}
        yAxisPrecision={yAxisPrecision}
        yAxisFormat={yAxisPrecision === 0 ? formats.noDecimals : undefined}
        fixedYRange={fixedYRange}
        participants={participants}
        context={context}
        onSeriesInspect={onInspect}
        noDataMessage={noDataMessage}
        tooltipContentGenerator={tooltipContentGenerator}
        stacked={stacked}
      />
      <RangeSliderInput
        {...wholeRange}
        className="full-width margin-t-20 margin-b-20"
        onChange={onChange}
      />
      <Totals {...result} />
    </>
  );
};

const BigGraph = styled.div`
  min-height: 12cm;
  & svg {
    min-height: 500px;
  }
  @media print {
    width: 100% !important;
    overflow: hidden;
  }
`;
const NoTotals = () => console.log("no totals configured ") || null;

const evaluateContext = (context, from) =>
  typeof from === "function" ? from(context) : from;

const asNameLabel = (names) => names.map((name) => ({ name, label: name }));

const doUnits = (param, context) =>
  typeof param === 'function' ? param(context) : param

const UsageGraph = ({
  items,
  range,
  participants,
  itemsFrom = "services",
  itemsValueFrom = "speed",
  graphTitle = null,
  yAxisUnits = "Mbps",
  yAxisFormat = undefined,
  context = undefined,
  onSeriesInspect = undefined,
  noDataMessage = undefined,
  stacked = true,
  tooltipContentGenerator = tooltipContentGeneratorForUnits,
  fixedYRange = undefined,
}) => {
  const laneItems = useMemo(
    () => !items || items.length === 0
      ? []
      : getFieldsLanes(itemsFrom, evaluateContext(context, itemsValueFrom), participants)(items),
    [items, itemsFrom, itemsValueFrom, participants, context]
  );

  if (!items || items.length === 0) {
    return <NoDataFound message={noDataMessage} />
  }

  const Chart = stacked === true ? NVStackedAreaChartDownsampled : NVLineChartDownsampled;
  const fields = participants === undefined
    ? getFieldsOf(itemsFrom)(items)
    : asNameLabel(participants);

  return (
    <UsageGraphLayout className="group margin-t-20">
      {graphTitle === null ? null : <h4>{graphTitle}</h4>}
      <BigGraph className="big graph">
        <Chart
          copyEndpoints={true}
          xAxisFormat={d3.timeFormat("%m/%d %H:%M")}
          xField="time"
          fields={fields}
          items={laneItems}
          range={range}
          yAxisUnits={yAxisUnits}
          yAxisFormat={yAxisFormat}
          tooltipContentGenerator={
            tooltipContentGenerator(
              yAxisUnits,
              yAxisFormat
            )
          }
          yAxisTopGap={0.05}
          onSeriesInspect={onSeriesInspect}
          fixedYRange={fixedYRange}
        />
      </BigGraph>
    </UsageGraphLayout>
  );
};

const ReportRequest = ({
  children,
  actions,
  provider,
  volumeOrRatio,
  hours,
  directions,
  categories,
  filter,
  output,
  target,
  policyType,
  ...rest
}) => {
  const { bqnId, clusterStatus } = useContext(BqnContext);
  const mode = clusterStatus?.mode;
  const [request, setRequest] = useState(null);
  const [range, setRange] = useState(null);
  const doLoad = () => {
    const doingRetrieval = retrieveRange(hours, mode, bqnId)
      .then((range) => setRange(range) || range)
      .then(() =>
        provider({
          volumeOrRatio,
          hours,
          directions,
          categories,
          filter,
          output,
          target,
          policyType,
          range,
          mode,
          bqnId
        })
      );
    setRequest(doingRetrieval);
  };
  // Nothing is asked while the BQN context has not loaded the BQN selected:
  // the commands would not carry its option, and their responses would be
  // those of the whole cluster. The effect runs again when it does.
  useEffect(() => {
    if (bqnId === undefined) {
      return;
    }

    doLoad();
    return actions.recv("do-load", doLoad);
  }, [
    hours,
    volumeOrRatio,
    directions,
    categories,
    filter,
    output,
    target,
    policyType,
    mode,
    bqnId
  ]);
  const context = {
    hours,
    directions,
    categories,
    filter,
    output,
    target,
    policyType,
    volumeOrRatio,
  };
  return request === null ? null : (
    <Request during={request}>
      {(result) => children({ ...result, ...rest, range, context })}
    </Request>
  );
};

const _defaultContext = {
  hours: 24,
  interval: 10,
  categories: 20,
  directions: "all",
  output: "ip-addresses",
  target: { subsId: null, addr: null },
  policyType: "rate",
};

export const buildInitialContext = (settings, searchParams, defaults) =>
  Object.fromEntries(
    Object.entries(defaults).map(([field, defaultValue]) => [
      field,
      (field in settings && settings[field].selected) ||
      (field === 'target' ? { subsId: searchParams.subsId, addr: searchParams.addr } : searchParams[field]) ||
      defaultValue,
    ])
  );
const ReportContext = ({
  provider,
  actions,
  enabled,
  settings = {},
  returnView = null,
  ...rest
}) => {
  let [searchParams] = useSearchParams();
  const hoursOfReturnView = useHoursOfReturnView(returnView);
  const [context, setContext] = useState(
    buildInitialContext(settings, toObjectLiteral(searchParams), {
      ..._defaultContext,
      ...(hoursOfReturnView === undefined ? {} : { hours: hoursOfReturnView }),
    })
  );
  const updateContext = ({
    volumeOrRatio,
    hours,
    directions,
    categories,
    filter,
    output,
    target,
    policyType,
  }) =>
    setContext((prev) => ({
      ...prev,
      ...(volumeOrRatio === undefined ? {} : { volumeOrRatio }),
      ...(hours === undefined ? {} : { hours }),
      ...(directions === undefined ? {} : { directions }),
      ...(categories === undefined ? {} : { categories }),
      ...(filter === undefined ? {} : { filter }),
      ...(output === undefined ? {} : { output }),
      ...(target === undefined ? {} : { target }),
      ...(policyType === undefined ? {} : { policyType }),
    }));
  return (
    <ReportContextControls
      {...context}
      settings={settings}
      onChange={updateContext}
      enabled={enabled}
      hasTwoWeeks
    >
      <ReportRequest
        provider={provider}
        {...context}
        actions={actions}
        {...rest}
      >
        {(result) => <ReportResult context={context} {...result} {...rest} />}
      </ReportRequest>
    </ReportContextControls>
  );
};

export default ReportContext;
